import { computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, EMPTY, finalize, of, pipe, switchMap, tap } from 'rxjs';
import { TokenStorage } from '../auth/token.storage';
import { LoginRequest, RegisterRequest } from '../models/auth.models';
import { AuthHttpService } from '../services/auth-http.service';

const session = TokenStorage.getSession();

type AuthState = {
  loading: boolean;
  error: string | null;
  email: string | null;
  tenantGuid: string | null;
  companyCode: string | null;
  userId: number | null;
  role: string | null;
  authenticated: boolean;
  loginSucceeded: boolean;
  registerSucceeded: boolean;
};

const initialState: AuthState = {
  loading: false,
  error: null,
  email: session.email,
  tenantGuid: session.tenantGuid,
  companyCode: session.companyCode ?? null,
  userId: session.userId,
  role: session.role,
  authenticated: TokenStorage.isAuthenticated(),
  loginSucceeded: false,
  registerSucceeded: false,
};

export const AuthStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed(store => ({
    isAuthenticated: computed(() => store.authenticated()),
    hasTenantContext: computed(() => !!store.tenantGuid()),
  })),
  withMethods((store, authHttp = inject(AuthHttpService), router = inject(Router)) => {
    const hydrateFromSession = (emailFallback?: string) => {
      const claims = TokenStorage.getSession();
      patchState(store, {
        email: claims.email ?? emailFallback ?? null,
        tenantGuid: claims.tenantGuid,
        companyCode: claims.companyCode ?? store.companyCode() ?? null,
        userId: claims.userId,
        role: claims.role,
        authenticated: true,
        loading: false,
        error: null,
      });
    };

    return {
      clearError(): void {
        patchState(store, { error: null });
      },

      resetFlags(): void {
        patchState(store, { loginSucceeded: false, registerSucceeded: false, error: null });
      },

      /**
       * Public tenant key for client context (deep links, future HRIS handoff).
       * Account API calls are still scoped by JWT <c>tenant_guid</c>.
       */
      requireTenantGuid(): string {
        const guid = store.tenantGuid() ?? TokenStorage.getTenantGuid();
        if (!guid) throw new Error('Tenant context is missing. Please sign in again.');
        return guid;
      },

      /** Keep session in sync when company/profile APIs return TenantGuid. */
      setTenantGuid(tenantGuid: string): void {
        const current = TokenStorage.getSession();
        TokenStorage.setSession({ ...current, tenantGuid });
        patchState(store, { tenantGuid });
      },

      setCompanyCode(companyCode: string | null): void {
        const current = TokenStorage.getSession();
        TokenStorage.setSession({ ...current, companyCode });
        patchState(store, { companyCode });
      },

      logout(): void {
        TokenStorage.clear();
        patchState(store, {
          email: null,
          tenantGuid: null,
          companyCode: null,
          userId: null,
          role: null,
          error: null,
          authenticated: false,
          loginSucceeded: false,
          registerSucceeded: false,
        });
        void router.navigate(['/login']);
      },

      login: rxMethod<LoginRequest>(
        pipe(
          tap(() =>
            patchState(store, {
              loading: true,
              error: null,
              loginSucceeded: false,
            }),
          ),
          switchMap(req =>
            authHttp.login(req).pipe(
              tap(token => {
                TokenStorage.setTokens(token.accessToken, token.refreshToken, token.expiresAt);
                hydrateFromSession(req.email);
                patchState(store, { loginSucceeded: true });
              }),
              catchError(err => {
                const message =
                  err?.error?.message ??
                  (err?.status === 401 ? 'Invalid email or password.' : 'Login failed. Please try again.');
                patchState(store, {
                  loading: false,
                  error: message,
                  authenticated: false,
                  tenantGuid: null,
                  userId: null,
                  role: null,
                });
                return EMPTY;
              }),
              finalize(() => patchState(store, { loading: false })),
            ),
          ),
        ),
      ),

      register: rxMethod<RegisterRequest>(
        pipe(
          tap(() =>
            patchState(store, {
              loading: true,
              error: null,
              registerSucceeded: false,
            }),
          ),
          switchMap(req =>
            authHttp.register(req).pipe(
              switchMap(reg => {
                const applyRegistrationSession = () => {
                  if (reg.tenantGuid) {
                    const current = TokenStorage.getSession();
                    TokenStorage.setSession({
                      ...current,
                      tenantGuid: reg.tenantGuid,
                      companyCode: reg.companyCode ?? current.companyCode ?? null,
                    });
                    patchState(store, {
                      tenantGuid: reg.tenantGuid,
                      companyCode: reg.companyCode ?? null,
                    });
                  }
                  patchState(store, { registerSucceeded: true });
                };

                // When outbound email is enabled, login is blocked until verify —
                // do not auto-login or the UI treats registration as failed.
                if (reg.emailVerificationRequired) {
                  applyRegistrationSession();
                  return of(null);
                }

                return authHttp.login({ email: req.adminEmail, password: req.password }).pipe(
                  tap(token => {
                    TokenStorage.setTokens(token.accessToken, token.refreshToken, token.expiresAt);
                    hydrateFromSession(req.adminEmail);
                    applyRegistrationSession();
                  }),
                );
              }),
              catchError(err => {
                const message =
                  err?.error?.message ??
                  (err?.status === 409
                    ? 'An account with this email already exists.'
                    : 'Registration failed. Please try again.');
                patchState(store, { loading: false, error: message });
                return EMPTY;
              }),
              finalize(() => patchState(store, { loading: false })),
            ),
          ),
        ),
      ),

      forgotPassword(email: string, onSuccess?: () => void): void {
        patchState(store, { loading: true, error: null });
        authHttp.forgotPassword(email).subscribe({
          next: () => {
            patchState(store, { loading: false, error: null });
            onSuccess?.();
          },
          error: err => {
            patchState(store, {
              loading: false,
              error: err?.error?.message ?? 'Unable to send reset link. Please try again.',
            });
          },
        });
      },

      resetPassword(
        body: { token: string; newPassword: string },
        onSuccess?: () => void,
      ): void {
        patchState(store, { loading: true, error: null });
        authHttp.resetPassword(body).subscribe({
          next: () => {
            patchState(store, { loading: false, error: null });
            onSuccess?.();
          },
          error: err => {
            patchState(store, {
              loading: false,
              error: err?.error?.message ?? 'Unable to reset password. The link may have expired.',
            });
          },
        });
      },
    };
  }),
);
